import { 
  Grid, 
  TextField, 
  Button, 
  Switch, 
  FormControlLabel, 
  Box, 
  Typography, 
  IconButton, 
  Divider,
  Paper,
  Stack
} from "@mui/material";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CircularProgress from '@mui/material/CircularProgress';
import { Form as FormikForm, useFormikContext } from "formik";
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';

import dayjs from 'dayjs';

import { CustomField } from '../../../inputs/CustomField';

export const ActivitiesForm = ({ handleChange, onClose }) => {
  const { values, setFieldValue, isSubmitting } = useFormikContext();

  return (
    <Paper elevation={0} sx={{ p: 0, minWidth: 0, width: '100%' }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        p: { xs: 2, sm: 3 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, width: { xs: 'calc(100% - 40px)', sm: 'auto' }, minWidth: 0 }}>
          <Box sx={{
            width: { xs: 40, sm: 48 },
            height: { xs: 40, sm: 48 },
            borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <EventIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant={{ xs: 'h6', sm: 'h5' }} fontWeight={600} color="text.primary" sx={{ fontSize: { xs: '1rem', sm: '1.5rem' }, lineHeight: 1.2 }}>
              {values._id ? 'Editar Actividad' : 'Nueva Actividad'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              {values._id ? 'Modifica los detalles de la actividad' : 'Completa la información para crear una nueva actividad'}
            </Typography>
          </Box>
        </Box>
        <IconButton 
          onClick={onClose}
          sx={{ 
            color: 'text.secondary',
            alignSelf: { xs: 'flex-end', sm: 'center' },
            position: { xs: 'absolute', sm: 'static' },
            top: { xs: 16, sm: 'auto' },
            right: { xs: 16, sm: 'auto' },
            '&:hover': { 
              backgroundColor: 'error.light',
              color: 'error.main'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Form Content */}
      <FormikForm>
        <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {/* Información Básica */}
            <Grid item xs={12} sx={{ minWidth: 0 }}>
              <Typography variant={{ xs: 'subtitle1', sm: 'h6' }} sx={{ mb: { xs: 1, sm: 2 }, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                <EventIcon color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
                Información Básica
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
              <CustomField 
                name="actividad" 
                label="Nombre de la Actividad" 
                fullWidth 
                as={TextField}
                value={values?.actividad || ""}
                size="small"
                InputProps={{
                  startAdornment: <EventIcon sx={{ color: 'text.secondary', mr: 1, display: { xs: 'none', sm: 'block' } }} />
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
              <CustomField 
                name="anfitrion" 
                label="Nombre del Anfitrión" 
                fullWidth 
                as={TextField}
                size="small"
                InputProps={{
                  startAdornment: <PersonIcon sx={{ color: 'text.secondary', mr: 1, display: { xs: 'none', sm: 'block' } }} />
                }}
              />
            </Grid>

            <Grid item xs={12} sx={{ minWidth: 0 }}>
              <CustomField 
                name="description" 
                multiline 
                rows={{ xs: 3, sm: 4 }}
                label="Descripción del Evento" 
                fullWidth 
                as={TextField} 
                value={values?.description || ""}
                size="small"
                InputProps={{
                  startAdornment: <DescriptionIcon sx={{ color: 'text.secondary', mr: 1, alignSelf: 'flex-start', mt: 1, display: { xs: 'none', sm: 'block' } }} />
                }}
              />
            </Grid>

            {/* Configuración */}
            <Grid item xs={12} sx={{ minWidth: 0 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant={{ xs: 'subtitle1', sm: 'h6' }} sx={{ mb: { xs: 1, sm: 2 }, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                <GroupIcon color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
                Configuración
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <CustomField 
                  name="lastEnrollDate" 
                  fullWidth 
                  onChange={(e) => {
                    setFieldValue("lastEnrollDate", e.$d)
                  }}
                  as={DatePicker} 
                  value={dayjs(values.lastEnrollDate)} 
                  label="Fecha Límite de Inscripción"
                  slotProps={{ 
                    textField: { 
                      size: 'small', 
                      fullWidth: true,
                      variant: 'outlined'
                    } 
                  }} 
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
              <Box sx={{ 
                border: '1px solid', 
                borderColor: 'divider', 
                borderRadius: 2, 
                p: { xs: 1, sm: 2 },
                minHeight: { xs: '40px', sm: '56px' },
                display: 'flex',
                alignItems: 'center',
                minWidth: 0
              }}>
                <FormControlLabel
                  sx={{ m: 0, flex: 1 }}
                  control={
                    <Switch
                      checked={values?.limitSpaces || false}
                      onChange={() => setFieldValue("limitSpaces", !(values?.limitSpaces || false))}
                      name="limitSpaces"
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant={{ xs: 'body2', sm: 'body1' }} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                      {values?.limitSpaces ? 'Cupos Limitados' : 'Cupos Ilimitados'}
                    </Typography>
                  }
                />
              </Box>
            </Grid>

            {values.limitSpaces && (
              <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                <CustomField
                  fullWidth
                  type="number"
                  label="Número Máximo de Participantes"
                  name="participantsNumber"
                  as={TextField}
                  value={values?.participantsNumber || 1}
                  size="small"
                  InputProps={{
                    startAdornment: <GroupIcon sx={{ color: 'text.secondary', mr: 1, display: { xs: 'none', sm: 'block' } }} />
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
              <Box sx={{ 
                border: '1px solid', 
                borderColor: 'divider', 
                borderRadius: 2, 
                p: { xs: 1, sm: 2 },
                minHeight: { xs: '40px', sm: '56px' },
                display: 'flex',
                alignItems: 'center',
                minWidth: 0
              }}>
                <FormControlLabel
                  sx={{ m: 0, flex: 1 }}
                  control={
                    <Switch
                      checked={values?.certifica_solo_confirmados || false}
                      onChange={() => setFieldValue("certifica_solo_confirmados", !(values?.certifica_solo_confirmados || false))}
                      name="certifica_solo_confirmados"
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant={{ xs: 'body2', sm: 'body1' }} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                      Certificar solo confirmados
                    </Typography>
                  }
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Footer */}
        <Box sx={{ 
          p: { xs: 1.5, sm: 3 }, 
          borderTop: '1px solid', 
          borderColor: 'divider',
          backgroundColor: 'grey.50'
        }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 2 }} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={onClose}
              size="medium"
              sx={{ minWidth: { xs: '100%', sm: 120 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
              fullWidth={{ xs: true, sm: false }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              type="submit"
              size="medium"
              disabled={isSubmitting}
              fullWidth={{ xs: true, sm: false }}
              sx={{ 
                minWidth: { xs: '100%', sm: 120 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                }
              }}
            >
              {isSubmitting && <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />}
              {isSubmitting ? 'Guardando...' : (values._id ? 'Actualizar' : 'Crear Actividad')}
            </Button>
          </Stack>
        </Box>
      </FormikForm>
    </Paper>
  );
}

export default ActivitiesForm;